import { useEffect, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Clock, LogOut, Sparkles, XCircle } from 'lucide-react';
import * as altaApi from '@/api/alta.api';
import type { Analisis, MiSolicitud, ModuloOfrecido } from '@/api/alta.api';
import { listWorkspaces } from '@/api/workspaces.api';
import { ApiError } from '@/api/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { useWorkspaceStore } from '@/store/workspace.store';
import { Button } from '@/components/ui/Button';
import { Card, CardBody } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { Textarea } from '@/components/ui/Textarea';
import { GrabadorDeAudio } from '@/components/alta/GrabadorDeAudio';

const CLAVE = ['alta', 'mi-solicitud'];
const mensaje = (err: unknown) => (err instanceof ApiError ? err.message : 'Algo falló. Revisa tu conexión y vuelve a probar.');

/**
 * «Mi alta»: la cuenta nueva, que todavía no tiene negocio, cuenta lo que
 * necesita (audio y/o texto), ve la propuesta y la manda para que la aprueben.
 */
export default function MiAlta() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const setWorkspaces = useWorkspaceStore((s) => s.setWorkspaces);
  const q = useQuery({ queryKey: CLAVE, queryFn: altaApi.miSolicitud, retry: false });

  // 404: esta cuenta ya no es un alta (la aprobaron, o tiene un negocio). Se
  // relee la lista de negocios: con alguno, al panel; sin ninguno, se dice
  // aquí y NO se rebota (el panel la mandaría de vuelta: sería un bucle).
  const sinSolicitud = q.error instanceof ApiError && q.error.status === 404;
  const negocios = useQuery({ queryKey: ['alta', 'negocios'], queryFn: listWorkspaces, enabled: sinSolicitud, retry: false });
  useEffect(() => {
    if (negocios.data) setWorkspaces(negocios.data);
  }, [negocios.data, setWorkspaces]);

  const entrar = async () => {
    setWorkspaces(await listWorkspaces());
    navigate('/', { replace: true });
  };

  let cuerpo: React.ReactNode;
  if (q.isLoading || (sinSolicitud && negocios.isLoading)) {
    cuerpo = <div className="flex justify-center py-16"><Spinner /></div>;
  } else if (sinSolicitud && negocios.data && negocios.data.items.length > 0) {
    return <Navigate to="/" replace />;
  } else if (sinSolicitud) {
    cuerpo = (
      <Aviso titulo="Tu cuenta todavía no tiene un negocio">
        Pídele acceso a quien te invitó, o sal y crea tu Conversia con otro correo.
      </Aviso>
    );
  } else if (q.error || !q.data) {
    cuerpo = (
      <Aviso titulo="No pudimos cargar tu solicitud">
        {mensaje(q.error)}{' '}
        <Button variant="outline" size="sm" className="mt-3" onClick={() => void q.refetch()}>Probar otra vez</Button>
      </Aviso>
    );
  } else if (q.data.estado === 'BORRADOR') {
    cuerpo = <Borrador solicitud={q.data} />;
  } else {
    cuerpo = <EstadoDeSolicitud solicitud={q.data} onEntrar={() => void entrar()} />;
  }

  return (
    <div className="min-h-full bg-surface-muted dark:bg-slate-950">
      <header className="flex items-center gap-3 border-b border-surface-border bg-white px-4 py-3 dark:border-slate-800 dark:bg-slate-900">
        <img src="/conversia-logo.svg" alt="" className="h-8 w-8" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">
            {q.data?.negocioNombre ?? 'Tu Conversia'}
          </p>
          {user?.email && <p className="truncate text-xs text-slate-500">{user.email}</p>}
        </div>
        <Button variant="ghost" size="sm" onClick={() => void logout()}>
          <LogOut className="h-4 w-4" /> Salir
        </Button>
      </header>
      <main className="mx-auto w-full max-w-2xl px-4 py-6">{cuerpo}</main>
    </div>
  );
}

function Aviso({ titulo, children, icono }: { titulo: string; children: React.ReactNode; icono?: React.ReactNode }) {
  return (
    <Card>
      <CardBody className="space-y-2 text-center">
        {icono}
        <h1 className="text-lg font-semibold text-slate-900 dark:text-slate-100">{titulo}</h1>
        <div className="text-sm text-slate-600 dark:text-slate-300">{children}</div>
      </CardBody>
    </Card>
  );
}

/** ENVIADA, RECHAZADA o APROBADA: ya no se edita nada, solo se informa. */
export function EstadoDeSolicitud({ solicitud, onEntrar }: { solicitud: MiSolicitud; onEntrar: () => void }) {
  if (solicitud.estado === 'APROBADA') {
    return (
      <Aviso titulo="¡Tu Conversia está lista!" icono={<CheckCircle2 className="mx-auto h-10 w-10 text-emerald-500" />}>
        <p>Aprobamos tu solicitud. Ya puedes entrar y empezar a usarla.</p>
        <Button className="mt-4" onClick={onEntrar}>Entrar a mi Conversia</Button>
      </Aviso>
    );
  }
  if (solicitud.estado === 'RECHAZADA') {
    return (
      <Aviso titulo="No pudimos aprobar tu solicitud" icono={<XCircle className="mx-auto h-10 w-10 text-red-500" />}>
        {solicitud.motivoRechazo && (
          <p className="rounded-md bg-slate-50 px-3 py-2 text-left dark:bg-slate-800">
            <span className="font-medium">El motivo:</span> {solicitud.motivoRechazo}
          </p>
        )}
        <p className="mt-3">
          Si tu negocio cambia o crees que fue un error, te contactaremos para verlo contigo.
        </p>
      </Aviso>
    );
  }
  return (
    <Aviso titulo="Recibimos tu solicitud" icono={<Clock className="mx-auto h-10 w-10 text-brand-500" />}>
      <p>Recibimos tu solicitud; te avisamos al aprobarla.</p>
      <p className="mt-2 text-xs text-slate-500">Puedes cerrar esta página. Cuando entres de nuevo, verás aquí cómo va.</p>
    </Aviso>
  );
}

/** Problemas → cómo los resuelve Conversia → módulos, con la frase del cliente. */
export function Propuesta({ analisis, nombres }: { analisis: Analisis; nombres: Record<string, string> }) {
  return (
    <div className="space-y-3">
      {analisis.resumen && <p className="text-sm text-slate-700 dark:text-slate-200">{analisis.resumen}</p>}
      {analisis.hallazgos.map((h, i) => (
        <div key={i} className="space-y-1.5 rounded-md border border-surface-border p-3 dark:border-slate-700">
          <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{h.problema}</p>
          <p className="text-sm italic text-slate-500 dark:text-slate-400">Tú dijiste: «{h.frase}»</p>
          <p className="text-sm text-slate-700 dark:text-slate-200">{h.solucion}</p>
          <div className="flex flex-wrap gap-1.5">
            {h.modulos.map((m) => (
              <span key={m} className="rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-900/40 dark:text-brand-300">
                {nombres[m] ?? m}
              </span>
            ))}
          </div>
        </div>
      ))}
      {analisis.hallazgos.length === 0 && (
        <p className="text-sm text-slate-500">No vimos ningún problema concreto en lo que contaste. Cuéntanos un poco más, o envíala así y la revisamos nosotros.</p>
      )}
    </div>
  );
}

/** Cuando no hay análisis automático: se dice claro y se puede enviar igual. */
export function SinAnalisis({ motivo }: { motivo: string }) {
  return (
    <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
      {motivo} Si quieres, marca abajo lo que te interesa; si no, nosotros elegimos lo que mejor te sirva.
    </p>
  );
}

function Paso({ n, titulo, children }: { n: number; titulo: string; children: React.ReactNode }) {
  return (
    <Card>
      <CardBody className="space-y-4">
        <h2 className="flex items-center gap-2 text-base font-semibold text-slate-900 dark:text-slate-100">
          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-600 text-xs text-white">{n}</span>
          {titulo}
        </h2>
        {children}
      </CardBody>
    </Card>
  );
}

function Borrador({ solicitud }: { solicitud: MiSolicitud }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [texto, setTexto] = useState(solicitud.textoCliente);
  const [elegidos, setElegidos] = useState<string[]>(solicitud.modulosPropuestos);
  const [motivoSinAnalisis, setMotivoSinAnalisis] = useState<string | null>(null);
  const catalogo = useQuery({ queryKey: ['alta', 'catalogo'], queryFn: altaApi.catalogo, staleTime: Infinity });
  const nombres = Object.fromEntries((catalogo.data ?? []).map((m: ModuloOfrecido) => [m.id, m.nombre]));

  const poner = (s: MiSolicitud) => qc.setQueryData(CLAVE, s);
  /** Lo escrito se guarda antes de analizar o enviar: que no se pierda nada. */
  const guardarSiCambio = async () => {
    if (texto !== solicitud.textoCliente) poner(await altaApi.guardarTexto(texto));
  };

  const guardar = useMutation({
    mutationFn: () => altaApi.guardarTexto(texto),
    onSuccess: (s) => {
      poner(s);
      toast.success('Guardado');
    },
    onError: (e) => toast.error('No se guardó tu texto', mensaje(e)),
  });
  const audio = useMutation({
    mutationFn: ({ blob, nombre }: { blob: Blob; nombre: string }) => altaApi.subirAudio(blob, nombre),
    onSuccess: (r) => {
      poner(r.solicitud);
      toast.success(r.mensaje);
    },
    onError: (e) => toast.error('No se subió el audio', mensaje(e)),
  });
  const analizar = useMutation({
    mutationFn: async () => {
      await guardarSiCambio();
      return altaApi.analizar();
    },
    onSuccess: (r) => {
      if (r.disponible) {
        poner(r.solicitud);
        setElegidos(r.analisis.modulos);
        setMotivoSinAnalisis(null);
      } else {
        setMotivoSinAnalisis(r.motivo);
      }
    },
    onError: (e) => {
      // 429: ya se analizó varias veces. Se dice y se puede enviar igual.
      if (e instanceof ApiError && e.status === 429) setMotivoSinAnalisis(e.message);
      else toast.error('No pudimos analizar tu solicitud', mensaje(e));
    },
  });
  const enviar = useMutation({
    mutationFn: async () => {
      await guardarSiCambio();
      return altaApi.enviar(elegidos);
    },
    onSuccess: poner,
    onError: (e) => toast.error('No se envió tu solicitud', mensaje(e)),
  });

  const hayAlgo = texto.trim().length > 0 || solicitud.audios.length > 0;
  const cambiar = (id: string) =>
    setElegidos((e) => (e.includes(id) ? e.filter((x) => x !== id) : [...e, id]));

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-bold text-slate-900 dark:text-slate-100">Cuéntanos tu negocio</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          Dinos qué vendes, cómo te escriben tus clientes y qué se te complica. Con eso te proponemos lo que mejor te sirve.
        </p>
      </div>

      <Paso n={1} titulo="Cuéntanos con tu voz o por escrito">
        <GrabadorDeAudio
          subiendo={audio.isPending}
          onSubir={(blob, nombre) => audio.mutateAsync({ blob, nombre })}
        />
        {solicitud.audios.length > 0 && (
          <ul className="space-y-1.5">
            {solicitud.audios.map((a, i) => (
              <li key={i} className="rounded-md bg-slate-50 px-3 py-2 text-sm dark:bg-slate-800">
                <span className="font-medium">Audio {i + 1}:</span>{' '}
                {a.transcripcion ? (
                  <span className="text-slate-600 dark:text-slate-300">«{a.transcripcion}»</span>
                ) : (
                  <span className="text-slate-500">sin transcribir todavía; lo escuchamos nosotros.</span>
                )}
              </li>
            ))}
          </ul>
        )}
        <Textarea
          label="O escríbelo aquí"
          rows={5}
          maxLength={20_000}
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Ej.: Tengo un colmado. Me escriben por WhatsApp y se me pierden los pedidos…"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={() => guardar.mutate()}
          loading={guardar.isPending}
          disabled={texto === solicitud.textoCliente}
        >
          Guardar lo escrito
        </Button>
      </Paso>

      <Paso n={2} titulo="Ver propuesta">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          Leemos lo que nos contaste y te mostramos qué problemas vemos y cómo Conversia los resuelve.
        </p>
        <Button onClick={() => analizar.mutate()} loading={analizar.isPending} disabled={!hayAlgo}>
          <Sparkles className="h-4 w-4" /> {solicitud.analisis ? 'Volver a ver la propuesta' : 'Ver mi propuesta'}
        </Button>
        {!hayAlgo && <p className="text-xs text-slate-500">Primero cuéntanos algo (un audio o unas líneas).</p>}
        {motivoSinAnalisis ? (
          <SinAnalisis motivo={motivoSinAnalisis} />
        ) : (
          solicitud.analisis && <Propuesta analisis={solicitud.analisis} nombres={nombres} />
        )}
        {(motivoSinAnalisis || solicitud.analisis) && catalogo.data && (
          <fieldset className="space-y-2">
            <legend className="mb-1 text-sm font-medium text-slate-800 dark:text-slate-200">Lo que vas a pedir</legend>
            {catalogo.data.map((m) => (
              <label key={m.id} className="flex cursor-pointer items-start gap-2 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5"
                  checked={elegidos.includes(m.id)}
                  onChange={() => cambiar(m.id)}
                />
                <span>
                  <span className="font-medium text-slate-900 dark:text-slate-100">{m.nombre}</span>
                  <span className="block text-xs text-slate-500">{m.descripcion}</span>
                </span>
              </label>
            ))}
          </fieldset>
        )}
      </Paso>

      <Paso n={3} titulo="Enviar">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          La revisamos y te avisamos al aprobarla. Después de enviarla ya no se puede cambiar.
        </p>
        <Button onClick={() => enviar.mutate()} loading={enviar.isPending} disabled={!hayAlgo}>
          Enviar mi solicitud
        </Button>
      </Paso>
    </div>
  );
}
